import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  FileText, 
  Upload, 
  Plus, 
  LayoutDashboard, 
  List, 
  Search,
  ChevronRight,
  Users,
  Calendar,
  DollarSign,
  CheckCircle,
  Trash2,
  Eye,
  Loader2,
  Wrench,
  Download,
  FileCheck,
  Settings2,
  FileOutput,
  Edit2,
  Palette,
  LogOut,
  Shield,
  GitBranch,
  UserCheck,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ExtractionReview, matchEntityType } from "./components/ExtractionReview";
import { StatusBadge } from "./components/StatusBadge";
import TemplateEditor from "@/TemplateEditor";
import { AmendmentsList, AmendmentDetail } from "@/Amendments";
import BulkImport from "@/BulkImport";
import DataManagement from "@/DataManagement";
import ContactDirectory from "@/ContactDirectory";
import ExportFormatEditor from "@/ExportFormatEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== HELPER FUNCTIONS ==============
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Failed to format date:', error);
    return dateStr;
  }
};

// ============== SIDEBAR COMPONENT ==============
const Sidebar = ({ currentFY, user, onLogout, loading }) => {
  const { branding } = useBranding();
  // Hide sidebar entirely while auth is still checking
  if (loading || !user) return null;
  const hasPerm = (p) => Array.isArray(user?.permissions) && user.permissions.includes(p);
  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src={branding.logo_url || '/gnosis-logo.png'} alt={branding.app_name || 'Logo'}
             onError={(e) => { e.currentTarget.src = '/gnosis-logo.png'; }}
             style={{
               height: `${Math.max(branding.logo_height || 64, 64)}px`,
               maxWidth: '200px',
               marginBottom: '8px',
               objectFit: branding.logo_fit || 'contain',
               background: 'transparent',
               padding: 0,
               display: 'block',
             }} />
        <div className="sub">{branding.app_name || 'Contract Registry'}</div>
      </div>
      <nav className="sidebar-nav">
        <div className="nav-label">Main Menu</div>
        <NavItem to="/" icon={<LayoutDashboard className="w-4 h-4" />} label="Dashboard" />
        <NavItem to="/contracts" icon={<FileText className="w-4 h-4" />} label="Work Orders" />
        {(hasPerm('contracts.create') || hasPerm('contracts.edit_all')) && (
          <>
            <div className="nav-label">Actions</div>
            {hasPerm('contracts.create') && <NavItem to="/create" icon={<Plus className="w-4 h-4" />} label="New Contract" />}
            {hasPerm('contracts.create') && <NavItem to="/upload" icon={<Upload className="w-4 h-4" />} label="Upload Proposal" />}
            {hasPerm('contracts.create') && <NavItem to="/import" icon={<FileText className="w-4 h-4" />} label="Bulk Import CSV" />}
            <NavItem to="/contacts" icon={<Users className="w-4 h-4" />} label="Contact Directory" />
          </>
        )}
        <div className="nav-label">Settings</div>
        <NavItem to="/templates" icon={<Settings2 className="w-4 h-4" />} label="Templates" />
        <NavItem to="/export-format" icon={<FileOutput className="w-4 h-4" />} label="Export Format" />
        {hasPerm('users.manage') && <NavItem to="/users" icon={<Shield className="w-4 h-4" />} label="User Management" />}
        {hasPerm('branding.manage') && <NavItem to="/branding" icon={<Palette className="w-4 h-4" />} label="Branding" />}
        {hasPerm('matrix.manage') && <NavItem to="/approval-matrix" icon={<GitBranch className="w-4 h-4" />} label="Approval Matrix" />}
        <NavItem to="/my-delegation" icon={<UserCheck className="w-4 h-4" />} label="My Delegation" />
        <NavItem to="/guide" icon={<BookOpen className="w-4 h-4" />} label="User Guide" />
        {hasPerm('data.manage') && <NavItem to="/data" icon={<Trash2 className="w-4 h-4" />} label="Data Management" />}
      </nav>
      <div className="sidebar-footer">
        <div style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gold)' }} data-testid="sidebar-user-name">{user.name}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.88)', letterSpacing: '.3px', marginTop: 2 }} data-testid="sidebar-user-role">{user.role_label}</div>
          <button onClick={onLogout} data-testid="sidebar-logout" style={{
            marginTop: 6, background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
            color: 'white', padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <LogOut className="w-3 h-3" /> Sign Out
          </button>
        </div>
        Current FY: <span className="fy">{currentFY}</span>
        <div style={{ fontSize: '10px', color: 'var(--text-light)', marginTop: '4px', opacity: 0.6 }}>&copy; Mahesh Gori</div>
      </div>
    </aside>
  );
};

const NavItem = ({ to, icon, label }) => {
  const isActive = window.location.pathname === to;
  return (
    <Link to={to} className={`nav-item ${isActive ? 'active' : ''}`} data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}>
      <span className="icon" style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span>{label}</span>
    </Link>
  );
};

// ============== DASHBOARD PAGE ==============
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentContracts, setRecentContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, contractsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/contracts`)
        ]);
        setStats(statsRes.data);
        setRecentContracts(contractsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--navy)' }} />
      </div>
    );
  }

  const getVendorName = (contract) => contract.agreement_header?.second_party?.name || 'N/A';
  const getTerms = (c) => c.amc_terms || c.manpower_terms || {};
  const getMonthlyValue = (contract) => getTerms(contract).monthly_value || 0;

  return (
    <div className="animate-fadeIn" data-testid="dashboard-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Gnosis Hospitality LLP — Contract Overview</p>
        </div>
        <Link
          to="/guide"
          data-testid="dashboard-help-link"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--gold-pale)', color: 'var(--navy)',
            padding: '8px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
            textDecoration: 'none', border: '1px solid var(--gold-light)',
          }}
        >
          <BookOpen className="w-4 h-4" />
          How to create &amp; approve — 2 min read
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card navy">
          <div className="stat-label">Total Work Orders</div>
          <div className="stat-value navy" data-testid="total-contracts">{stats?.total_contracts || 0}</div>
          <div className="stat-sub">All time</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Active</div>
          <div className="stat-value green" data-testid="active-contracts">{stats?.active_contracts || 0}</div>
          <div className="stat-sub">Currently running</div>
        </div>
        <div className="stat-card gold">
          <div className="stat-label">Monthly Outflow (Active)</div>
          <div className="stat-value gold" data-testid="monthly-outflow">{formatCurrency(stats?.monthly_outflow)}</div>
          <div className="stat-sub">incl. GST: {formatCurrency(stats?.monthly_outflow_gst)}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">Draft / Expired</div>
          <div className="stat-value" style={{ color: 'var(--amber)' }} data-testid="draft-expired">{(stats?.draft_contracts || 0) + (stats?.expired_contracts || 0)}</div>
          <div className="stat-sub">{stats?.draft_contracts || 0} draft · {stats?.expired_contracts || 0} expired</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section-hdr">
        <h2>Quick Actions</h2>
      </div>
      <div className="quick-actions" style={{ marginBottom: '24px' }}>
        <div className="quick-action-card" onClick={() => navigate('/create')} data-testid="quick-action-new-wo">
          <div className="quick-action-icon svc"><Plus className="w-6 h-6" /></div>
          <div>
            <div className="quick-action-title">New Work Order</div>
            <div className="quick-action-desc">Create a unified service agreement — housekeeping, AMC, security, F&B, etc.</div>
          </div>
        </div>
        <div className="quick-action-card" onClick={() => navigate('/upload')} data-testid="quick-action-upload">
          <div className="quick-action-icon amc"><Upload className="w-6 h-6" /></div>
          <div>
            <div className="quick-action-title">Upload Vendor Proposal</div>
            <div className="quick-action-desc">Extract contract data from a PDF or image using AI</div>
          </div>
        </div>
      </div>

      {/* Recent Contracts */}
      <div className="section-hdr">
        <h2>Recent Contracts</h2>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{recentContracts.length} of {stats?.total_contracts || 0} contracts</span>
      </div>
      <div className="contract-table-wrap">
        {recentContracts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>No contracts found</p>
            <small>Create your first contract to get started</small>
          </div>
        ) : (
          <table className="contract-table">
            <thead>
              <tr>
                <th>Work Order No.</th>
                <th>Vendor / Agency</th>
                <th>Period</th>
                <th style={{ textAlign: 'right' }}>Monthly Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentContracts.map((contract) => (
                <tr key={contract.id} onClick={() => navigate(`/contracts/${contract.id}`)}>
                  <td><span className="contract-num">{contract.contract_number}</span></td>
                  <td>
                    <div className="vendor-name">{getVendorName(contract)}</div>
                    <div className="vendor-meta">{contract.agreement_header?.second_party?.type || ''}</div>
                  </td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px' }}>
                    {formatDate(getTerms(contract).start_date)} — {formatDate(getTerms(contract).end_date)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>{formatCurrency(getMonthlyValue(contract))}</td>
                  <td><StatusBadge status={contract.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============== UPLOAD PROPOSAL PAGE ==============
const buildReview = (data) => {
  const t = data.manpower_terms || data.amc_terms || {};
  const equipment = [t.equipment_count, t.equipment_name].filter(Boolean).join(' x ');
  return {
    vendor: { ...(data.agreement_header?.second_party || {}), type: matchEntityType(data.agreement_header?.second_party?.type) },
    terms: {
      scope: t.description_of_services || t.scope_description || (equipment ? `${t.amc_type ? t.amc_type + ' AMC for ' : ''}${equipment}` : ''),
      start_date: t.start_date || '', end_date: t.end_date || '',
      monthly_value: t.monthly_value || (t.annual_value ? Math.round(t.annual_value / 12) : 0),
      gst_rate: t.gst_rate ?? 18, pm_frequency: t.pm_frequency || '', response_time: t.response_time || '',
      resolution_time: t.resolution_time || '', special_conditions: t.special_conditions || '',
    },
  };
};

const UploadProposal = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [review, setReview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, []);

  const handleFile = (selectedFile) => {
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Invalid file type. Please upload PDF or image files.");
      return;
    }
    setFile(selectedFile);
    setExtractedData(null);
  };

  const handleExtract = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setExtractedData(response.data);
      setReview(buildReview(response.data));
      toast.success("Data extracted — please review the details");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract data from proposal");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateContract = () => {
    const { scope, ...terms } = review.terms;
    const key = extractedData.manpower_terms ? 'manpower_terms' : 'amc_terms';
    const merged = {
      ...extractedData,
      agreement_header: { ...extractedData.agreement_header, second_party: { ...extractedData.agreement_header?.second_party, ...review.vendor } },
      [key]: { ...(extractedData[key] || {}), ...terms, monthly_value: Number(terms.monthly_value) || 0, gst_rate: terms.gst_rate === '' ? 18 : Number(terms.gst_rate), description_of_services: scope, scope_description: scope },
    };
    navigate('/create', { state: { extractedData: merged } });
  };

  const handleReset = () => { setFile(null); setExtractedData(null); setReview(null); };

  if (extractedData && review && file) {
    return (
      <div className="animate-fadeIn" data-testid="upload-page">
        <div className="breadcrumb">
          <Link to="/">Dashboard</Link><span>›</span><Link to="/upload" onClick={handleReset}>Upload Proposal</Link><span>›</span><span>Review</span>
        </div>
        <div className="page-header">
          <h1 className="page-title">Review Extracted Details</h1>
          <p className="page-subtitle">Compare the AI-extracted fields with the proposal and correct anything before creating the Work Order</p>
        </div>
        <ExtractionReview file={file} review={review} setReview={setReview} confidence={extractedData.confidence}
                          onCreate={handleCreateContract} onReset={handleReset} />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="upload-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>›</span>
        <span>Upload Proposal</span>
      </div>
      <div className="page-header">
        <h1 className="page-title">Upload Vendor Proposal</h1>
        <p className="page-subtitle">Upload a proposal document and extract contract data using AI</p>
      </div>

      <div className="form-card">
        <div className="form-body-inner">
          <div
            className={`upload-zone ${dragActive ? 'dragging' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
            data-testid="upload-dropzone"
          >
            <input
              id="file-input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
              style={{ display: 'none' }}
            />
            {uploading ? (
              <>
                <div className="upload-icon"><Loader2 className="w-10 h-10 animate-spin" /></div>
                <p className="upload-text">Extracting data with AI...</p>
              </>
            ) : file ? (
              <>
                <div className="upload-icon">📄</div>
                <p className="upload-text"><strong>{file.name}</strong></p>
                <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>{(file.size / 1024).toFixed(1)} KB</p>
              </>
            ) : (
              <>
                <div className="upload-icon">📤</div>
                <p className="upload-text"><strong>Click to upload</strong> or drag and drop</p>
                <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>PDF, PNG, JPEG, WEBP</p>
              </>
            )}
          </div>

          {file && !extractedData && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button className="btn btn-primary" onClick={handleExtract} disabled={uploading} data-testid="extract-button">
                {uploading ? 'Extracting...' : '✨ Extract Data with AI'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ============== CREATE/EDIT CONTRACT PAGE ==============

// ============== CONTRACTS LIST PAGE ==============
const ContractsList = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fyFilter, setFyFilter] = useState('');
  const [fys, setFys] = useState([]);
  const navigate = useNavigate();

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (fyFilter) params.append('fy', fyFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/contracts?${params.toString()}`);
      setContracts(response.data);
    } catch (error) {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
    axios.get(`${API}/contracts/fys`).then(res => setFys(res.data.financial_years || []));
  }, [statusFilter, fyFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContracts();
  };

  const getVendorName = (contract) => contract.agreement_header?.second_party?.name || 'N/A';
  const getTerms = (c) => c.amc_terms || c.manpower_terms || {};
  const getMonthlyValue = (contract) => getTerms(contract).monthly_value || 0;

  return (
    <div className="animate-fadeIn" data-testid="contracts-list-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>›</span>
        <span>All Contracts</span>
      </div>
      <div className="page-header">
        <h1 className="page-title">All Work Orders</h1>
        <p className="page-subtitle">Gnosis Hospitality LLP — Contract Registry</p>
      </div>

      <form onSubmit={handleSearch} className="filter-bar">
        <div className="search-wrap">
          <Search className="search-icon w-4 h-4" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor name or work order no..." data-testid="search-input" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-status">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="terminated">Terminated</option>
        </select>
        <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} data-testid="filter-fy">
          <option value="">All FY</option>
          {fys.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
        </select>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/create')}>
          + New Work Order
        </button>
      </form>

      <div className="section-hdr">
        <h2>Work Orders</h2>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{contracts.length} work orders</span>
      </div>

      <div className="contract-table-wrap">
        {loading ? (
          <div className="empty-state"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : contracts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>No contracts found</p>
            <small>Try adjusting filters or create a new contract</small>
          </div>
        ) : (
          <table className="contract-table">
            <thead>
              <tr>
                <th>Work Order No.</th>
                <th>Vendor / Agency</th>
                <th>Period</th>
                <th style={{ textAlign: 'right' }}>Monthly Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract) => (
                <tr key={contract.id} onClick={() => navigate(`/contracts/${contract.id}`)}>
                  <td><span className="contract-num">{contract.contract_number}</span></td>
                  <td>
                    <div className="vendor-name">{getVendorName(contract)}</div>
                    <div className="vendor-meta">{contract.agreement_header?.second_party?.type || ''}</div>
                  </td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px' }}>
                    {formatDate(getTerms(contract).start_date)} — {formatDate(getTerms(contract).end_date)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>{formatCurrency(getMonthlyValue(contract))}</td>
                  <td><StatusBadge status={contract.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============== CONTRACT DETAIL PAGE ==============
const ContractDetail = () => {
  const { id } = useParams();
  const [contract, setContract] = useState(null);
  const [departmentHead, setDepartmentHead] = useState(null);
  const [annexures, setAnnexures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workflowNote, setWorkflowNote] = useState('');
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const [contractRes, deptHeadRes, annexRes] = await Promise.all([
          axios.get(`${API}/contracts/${id}`),
          axios.get(`${API}/contracts/${id}/department-head`).catch(() => ({ data: { department_head: null } })),
          axios.get(`${API}/contracts/${id}/annexures`).catch(() => ({ data: [] }))
        ]);
        setContract(contractRes.data);
        setDepartmentHead(deptHeadRes.data.department_head);
        setAnnexures(annexRes.data);
      } catch (error) {
        toast.error("Failed to load contract");
        navigate('/contracts');
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this contract? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/contracts/${id}`);
      toast.success("Contract deleted");
      navigate('/contracts');
    } catch (error) {
      toast.error("Failed to delete contract");
    }
  };

  const handleWorkflow = async (action) => {
    if (action === 'reject' && !workflowNote.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    setWorkflowBusy(true);
    try {
      const r = await axios.post(`${API}/contracts/${id}/${action}`, { note: workflowNote });
      toast.success(r.data.message || `Contract ${action}d`);
      setContract({ ...contract, status: r.data.status });
      setWorkflowNote('');
      setShowRejectBox(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally { setWorkflowBusy(false); }
  };

  const handleExport = async (format = 'word') => {
    try {
      const ext = format === 'pdf' ? 'pdf' : 'word';
      toast.info(`Generating ${format.toUpperCase()} document...`);
      
      const response = await fetch(`${API}/contracts/${id}/export/${ext}`);
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const fileExt = format === 'pdf' ? '.pdf' : '.docx';
      const filename = `${contract.contract_number.replace(/\//g, '_')}${fileExt}`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export contract. Please try again.");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (!contract) return null;

  const vendor = contract.agreement_header?.second_party || {};
  const terms = contract.amc_terms || contract.manpower_terms || {};
  const monthlyValue = terms?.monthly_value || 0;
  const gstRate = terms?.gst_rate || 18;
  const billingFreq = terms?.billing_frequency || 'monthly';
  const freqLabel = billingFreq === 'yearly' ? 'Yearly' : billingFreq === 'halfyearly' ? 'Half-Yearly' : billingFreq === 'quarterly' ? 'Quarterly' : 'Monthly';
  const annualValue = billingFreq === 'yearly' ? monthlyValue : billingFreq === 'halfyearly' ? monthlyValue * 2 : billingFreq === 'quarterly' ? monthlyValue * 4 : monthlyValue * 12;

  return (
    <div className="animate-fadeIn" data-testid="contract-detail-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>&rsaquo;</span>
        <Link to="/contracts">Work Orders</Link>
        <span>&rsaquo;</span>
        <span>{contract.contract_number}</span>
      </div>

      <div className="detail-card">
        <div className="detail-header-band">
          <div className="dh-left">
            <div className="contract-num-lg">{contract.contract_number}</div>
            <div className="vendor-lg">{vendor.name || 'N/A'}</div>
            <div className="type-lg">
              Work Order / Service Agreement &nbsp;&middot;&nbsp; FY {contract.financial_year}
            </div>
          </div>
          <div className="dh-right">
            <StatusBadge status={contract.status} style={{ fontSize: '13px', padding: '5px 14px' }} testId="contract-status-badge" />
          </div>
        </div>

        {/* Workflow action panel — shown to Procurement (submit) or Finance (approve/reject) */}
        {(
          (contract.status === 'draft' || contract.status === 'rejected') && hasPermission('contracts.submit')
        ) || (
          contract.status === 'pending_finance' && (hasPermission('contracts.approve') || hasPermission('contracts.approve_tier1'))
        ) ? (
          <div style={{ background: '#f8f9fa', borderBottom: '1px solid #eef1f5', padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
               data-testid="workflow-panel">
            <div style={{ fontSize: 12, color: '#666', flexBasis: '100%', marginBottom: showRejectBox ? 0 : -4 }}>
              {contract.status === 'draft' && 'Ready to submit for finance approval?'}
              {contract.status === 'rejected' && (
                <span style={{ color: '#b91c1c' }}>Rejected. Reason: {contract.rejection_reason || 'not stated'}. Revise and resubmit.</span>
              )}
              {contract.status === 'pending_finance' && 'This Work Order is awaiting your review. Add an optional note and Approve, or provide a reason and Reject.'}
            </div>
            {!showRejectBox && (
              <input
                type="text" value={workflowNote} onChange={e => setWorkflowNote(e.target.value)}
                placeholder={contract.status === 'pending_finance' ? 'Optional note for audit log' : 'Optional note'}
                data-testid="workflow-note"
                style={{ flex: 1, minWidth: 200, height: 36, padding: '0 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
              />
            )}
            {contract.status !== 'pending_finance' && hasPermission('contracts.submit') && (
              <button onClick={() => handleWorkflow('submit')} disabled={workflowBusy} data-testid="workflow-submit"
                      style={{ height: 36, background: 'var(--navy)', color: '#fff', border: 'none', padding: '0 18px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {workflowBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Submit for Finance Approval'}
              </button>
            )}
            {contract.status === 'pending_finance' && (hasPermission('contracts.approve') || hasPermission('contracts.approve_tier1')) && !showRejectBox && (
              <>
                <button onClick={() => handleWorkflow('approve')} disabled={workflowBusy} data-testid="workflow-approve"
                        style={{ height: 36, background: '#065f46', color: '#fff', border: 'none', padding: '0 18px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  {workflowBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Approve'}
                </button>
                <button onClick={() => { setShowRejectBox(true); setWorkflowNote(''); }} data-testid="workflow-reject-open"
                        style={{ height: 36, background: '#fff', color: '#991b1b', border: '1px solid #991b1b', padding: '0 18px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  Reject
                </button>
              </>
            )}
            {contract.status === 'pending_finance' && showRejectBox && (
              <div style={{ flexBasis: '100%', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text" value={workflowNote} onChange={e => setWorkflowNote(e.target.value)}
                  placeholder="Rejection reason (required)" autoFocus data-testid="reject-reason"
                  style={{ flex: 1, height: 36, padding: '0 10px', border: '1px solid #991b1b', borderRadius: 6, fontSize: 13 }}
                />
                <button onClick={() => handleWorkflow('reject')} disabled={workflowBusy} data-testid="workflow-reject-confirm"
                        style={{ height: 36, background: '#991b1b', color: '#fff', border: 'none', padding: '0 18px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  {workflowBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Confirm Reject'}
                </button>
                <button onClick={() => { setShowRejectBox(false); setWorkflowNote(''); }} data-testid="workflow-reject-cancel"
                        style={{ height: 36, background: '#fff', color: '#666', border: '1px solid #ddd', padding: '0 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        ) : null}

        <div className="detail-body-inner">
          {/* Financial Callout */}
          <div className="fin-callout">
            <div className="fin-item">
              <div className="fl">{freqLabel} (excl. GST)</div>
              <div className="fv">{formatCurrency(monthlyValue)}</div>
            </div>
            <div className="fin-item">
              <div className="fl">GST @ {gstRate}%</div>
              <div className="fv">{formatCurrency(monthlyValue * gstRate / 100)}</div>
            </div>
            <div className="fin-item">
              <div className="fl">{freqLabel} Total</div>
              <div className="fv">{formatCurrency(monthlyValue * (1 + gstRate / 100))}</div>
            </div>
            <div className="fin-item">
              <div className="fl">Annual Total (incl. GST)</div>
              <div className="fv total">{formatCurrency(annualValue * (1 + gstRate / 100))}</div>
            </div>
          </div>

          <div className="detail-section-title"><Calendar className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Contract Details</div>
          <div className="info-grid">
            <div className="info-row"><div className="info-k">Agreement Date</div><div className="info-v mono">{formatDate(contract.agreement_header?.agreement_date)}</div></div>
            <div className="info-row"><div className="info-k">Contract Period</div><div className="info-v mono">{formatDate(terms?.start_date)} — {formatDate(terms?.end_date)}</div></div>
            <div className="info-row"><div className="info-k">Financial Year</div><div className="info-v mono">{contract.financial_year}</div></div>
            {departmentHead && (
              <div className="info-row">
                <div className="info-k">Department Head</div>
                <div className="info-v highlight">
                  {departmentHead.name}
                  {departmentHead.role && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — {departmentHead.role}</span>}
                  {departmentHead.phone && <span style={{ color: 'var(--text-light)', fontWeight: 400, marginLeft: '8px', fontSize: '12px' }}>{departmentHead.phone}</span>}
                </div>
              </div>
            )}
          </div>

          <div className="detail-section-title"><Users className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Service Provider</div>
          <div className="info-grid">
            <div className="info-row"><div className="info-k">Company Name</div><div className="info-v highlight">{vendor.name || 'N/A'}</div></div>
            <div className="info-row"><div className="info-k">Entity Type</div><div className="info-v">{vendor.type || 'N/A'}</div></div>
            <div className="info-row"><div className="info-k">Registered Address</div><div className="info-v">{vendor.registered_office || 'N/A'}</div></div>
            <div className="info-row"><div className="info-k">Authorized Signatory</div><div className="info-v">{vendor.representative_name || 'N/A'}{vendor.representative_designation ? `, ${vendor.representative_designation}` : ''}</div></div>
            <div className="info-row"><div className="info-k">GSTIN</div><div className="info-v mono">{vendor.gstin || 'N/A'}</div></div>
            <div className="info-row"><div className="info-k">PAN</div><div className="info-v mono">{vendor.pan || 'N/A'}</div></div>
          </div>

          {/* Scope of Work */}
          {(terms?.scope_description || terms?.description_of_services || terms?.vendor_conditions || terms?.facilities_provided || terms?.special_conditions) && (
            <>
              <div className="detail-section-title"><FileText className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Scope of Work</div>
              <div className="info-grid" style={{ gridTemplateColumns: '1fr' }}>
                {(terms?.scope_description || terms?.description_of_services) && (
                  <div className="info-row"><div className="info-k">Description</div><div className="info-v" style={{ whiteSpace: 'pre-wrap' }}>{terms.scope_description || terms.description_of_services}</div></div>
                )}
                {terms?.vendor_conditions && <div className="info-row"><div className="info-k">Vendor Conditions</div><div className="info-v">{terms.vendor_conditions}</div></div>}
                {terms?.facilities_provided && <div className="info-row"><div className="info-k">Facilities by Gnosis</div><div className="info-v">{terms.facilities_provided}</div></div>}
                {terms?.special_conditions && <div className="info-row"><div className="info-k">Special Conditions</div><div className="info-v">{terms.special_conditions}</div></div>}
              </div>
            </>
          )}

          {/* Maintenance Details */}
          {(terms?.amc_type || terms?.pm_frequency || terms?.response_time || terms?.resolution_time) && (
            <>
              <div className="detail-section-title"><Wrench className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Maintenance Terms</div>
              <div className="info-grid">
                {terms?.amc_type && (() => {
                  const typeMap = { 'Comprehensive': 'CAMC', 'Non-Comprehensive': 'NCAMC' };
                  const displayType = typeMap[terms.amc_type] || terms.amc_type;
                  return <div className="info-row"><div className="info-k">Work Order Type</div><div className="info-v"><span className={`badge ${displayType === 'CAMC' ? 'badge-amc' : displayType === 'NCAMC' ? 'badge-svc' : 'badge-draft'}`}>{displayType}</span></div></div>;
                })()}
                {terms?.pm_frequency && <div className="info-row"><div className="info-k">PM Frequency</div><div className="info-v">{terms.pm_frequency}</div></div>}
                {terms?.response_time && <div className="info-row"><div className="info-k">Response Time</div><div className="info-v">{terms.response_time}</div></div>}
                {terms?.resolution_time && <div className="info-row"><div className="info-k">Resolution Time</div><div className="info-v">{terms.resolution_time}</div></div>}
              </div>
            </>
          )}

          {/* PM Activities */}
          {terms?.preventive_maintenance_activities?.length > 0 && terms.preventive_maintenance_activities.some(a => a) && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                PM Activities
              </div>
              <div style={{ background: 'var(--gray-light)', borderRadius: 'var(--radius)', padding: '12px 16px' }}>
                {terms.preventive_maintenance_activities.filter(a => a).map((activity, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '8px', padding: '4px 0', fontSize: '13px' }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: 'var(--navy)', fontWeight: 500, minWidth: '20px' }}>{idx + 1}.</span>
                    <span>{activity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {terms?.breakdown_maintenance_obligations && (
            <div className="info-grid" style={{ gridTemplateColumns: '1fr', marginTop: '12px' }}>
              <div className="info-row"><div className="info-k">Breakdown Obligations</div><div className="info-v" style={{ whiteSpace: 'pre-wrap' }}>{terms.breakdown_maintenance_obligations}</div></div>
            </div>
          )}

          {/* Equipment Annexure Table */}
          {terms?.equipment_list && terms.equipment_list.length > 0 ? (
            <>
              <div className="detail-section-title">Annexure-I: Equipment Schedule</div>
              <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <table className="contract-table" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Equipment</th>
                      <th>Make</th>
                      <th>Model</th>
                      <th>Serial No.</th>
                      <th>Location</th>
                      <th>Qty/Capacity</th>
                      <th>Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terms.equipment_list.map((eq, idx) => (
                      <tr key={idx} style={{ cursor: 'default' }}>
                        <td style={{ fontFamily: "'DM Mono', monospace" }}>{idx + 1}</td>
                        <td style={{ fontWeight: 500 }}>{eq.equipment_name || '-'}</td>
                        <td>{eq.make || '-'}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px' }}>{eq.model || '-'}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px' }}>{eq.serial_number || '-'}</td>
                        <td>{eq.location || '-'}</td>
                        <td>{eq.quantity || '-'}</td>
                        <td>
                          <span className={`badge ${eq.scope_type === 'Comprehensive' ? 'badge-amc' : 'badge-svc'}`}>
                            {eq.scope_type === 'Comprehensive' ? 'Comp' : 'Non-Comp'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : terms?.annexure_attached_separately ? (
            <div style={{ 
              marginTop: '16px',
              padding: '16px 20px', 
              background: 'var(--gold-pale)', 
              border: '1px solid var(--gold)', 
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <FileText className="w-5 h-5" style={{ color: 'var(--amber)' }} />
              <div>
                <div style={{ fontWeight: 500, color: 'var(--amber)' }}>Annexure-I: Equipment Schedule</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>To be attached separately with the contract document</div>
              </div>
            </div>
          ) : null}

          {/* Schedule of Rates - Manpower */}
          {terms?.manpower_rates?.length > 0 && (
            <>
              <div className="detail-section-title"><DollarSign className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Schedule of Rates — Manpower</div>
              <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
                <table className="contract-table" style={{ marginBottom: 0, fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>#</th><th>Designation / Service</th><th>Count</th><th>UOM</th><th style={{ textAlign: 'right' }}>Rate</th><th>Est. Days</th><th style={{ textAlign: 'right' }}>Estimated Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terms.manpower_rates.map((r, i) => {
                      const estVal = r.uom === 'Per Day' ? (r.quantity || 0) * (r.rate_per_unit || 0) * (r.estimated_days || 26)
                        : r.uom === 'Per Hour' ? (r.quantity || 0) * (r.rate_per_unit || 0) * (r.estimated_days || 26)
                        : (r.quantity || 0) * (r.total_ctc || 0);
                      return (
                        <tr key={i} style={{ cursor: 'default' }}>
                          <td>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{r.designation}</td>
                          <td>{r.quantity}</td>
                          <td><span className="badge badge-draft">{r.uom || 'Monthly'}</span></td>
                          <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>{formatCurrency(r.uom === 'Per Day' || r.uom === 'Per Hour' ? r.rate_per_unit : r.total_ctc)}</td>
                          <td>{(r.uom === 'Per Day' || r.uom === 'Per Hour') ? r.estimated_days || 26 : '—'}</td>
                          <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{formatCurrency(estVal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--gray-light)', fontWeight: 600 }}>
                      <td></td><td>TOTAL</td><td>{terms.manpower_rates.reduce((s, r) => s + (r.quantity || 0), 0)}</td>
                      <td colSpan={3}></td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                        {formatCurrency(terms.manpower_rates.reduce((s, r) => {
                          if (r.uom === 'Per Day' || r.uom === 'Per Hour') return s + (r.quantity || 0) * (r.rate_per_unit || 0) * (r.estimated_days || 26);
                          return s + (r.quantity || 0) * (r.total_ctc || 0);
                        }, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {/* Other Charges */}
          {terms?.other_charges?.length > 0 && (
            <>
              <div className="detail-section-title" style={{ marginTop: '16px' }}><DollarSign className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Other Charges (Estimated / On Actuals)</div>
              <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
                <table className="contract-table" style={{ marginBottom: 0, fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>#</th><th>Description</th><th>UOM</th><th style={{ textAlign: 'right' }}>Rate</th><th>Est. Qty</th><th style={{ textAlign: 'right' }}>Estimated Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terms.other_charges.map((r, i) => (
                      <tr key={i} style={{ cursor: 'default' }}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{r.description}</td>
                        <td><span className="badge badge-draft">{r.uom}</span></td>
                        <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>{formatCurrency(r.rate)}</td>
                        <td>{r.estimated_qty}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.estimated_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--gold-pale)', fontWeight: 600 }}>
                      <td></td><td colSpan={4}>TOTAL ESTIMATED (ON ACTUALS)</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                        {formatCurrency(terms.other_charges.reduce((s, r) => s + (r.estimated_value || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {/* Schedule of Rates - Rate Card */}
          {terms?.rate_card_items?.length > 0 && (
            <>
              <div className="detail-section-title"><DollarSign className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Schedule of Rates — Rate Card</div>
              <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
                <table className="contract-table" style={{ marginBottom: 0, fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>#</th><th>Item / Service</th><th>Description</th><th>Unit</th><th style={{ textAlign: 'right' }}>Rate</th><th>Qty</th><th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terms.rate_card_items.map((r, i) => (
                      <tr key={i} style={{ cursor: 'default' }}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{r.item_name}</td>
                        <td>{r.description}</td>
                        <td>{r.unit}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>{formatCurrency(r.rate)}</td>
                        <td>{r.quantity}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--gray-light)', fontWeight: 600 }}>
                      <td></td><td colSpan={5}>TOTAL</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                        {formatCurrency(terms.rate_card_items.reduce((s, r) => s + (r.amount || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {/* File Annexures */}
          {annexures.length > 0 && (
            <>
              <div className="detail-section-title"><FileText className="w-4 h-4 inline" style={{ marginRight: '6px' }} />Attached Annexures ({annexures.length})</div>
              <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <table className="contract-table" style={{ marginBottom: 0, fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>#</th><th>Document</th><th>Type</th><th>Size</th><th>Uploaded</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {annexures.map((a, i) => (
                      <tr key={a.id} style={{ cursor: 'default' }}>
                        <td style={{ fontFamily: "'DM Mono', monospace" }}>Annexure-{a.annexure_number || i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{a.label || a.original_filename}</td>
                        <td><span className="badge badge-draft">{(a.file_type || '').toUpperCase()}</span></td>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>{a.file_size ? `${(a.file_size / 1024).toFixed(1)} KB` : '-'}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>{(a.created_at || '').substring(0, 10)}</td>
                        <td>
                          <a href={`${API}/annexures/${a.id}/download`} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 10px' }}>
                            <Download className="w-3 h-3" style={{ marginRight: '4px' }} /> Download
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Amendments Section */}
          <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--gray-border)' }}>
            <AmendmentsList contractId={id} contractNumber={contract.contract_number} />
          </div>
        </div>

        <div className="detail-footer">
          <button className="btn btn-ghost" onClick={() => navigate('/contracts')}>← Back</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary" onClick={() => navigate(`/contracts/${id}/edit`)} data-testid="edit-btn"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
            <button className="btn btn-gold" onClick={() => handleExport('word')} data-testid="export-word-btn">
              <Download className="w-4 h-4" style={{ marginRight: '6px', display: 'inline' }} />
              Word (.docx)
            </button>
            <button className="btn btn-gold" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn"
              style={{ background: '#c0392b' }}>
              <Download className="w-4 h-4" style={{ marginRight: '6px', display: 'inline' }} />
              PDF
            </button>
            <button className="btn btn-danger" onClick={handleDelete} data-testid="delete-btn">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN APP ==============
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import MagicApprove from './pages/MagicApprove';
import UserManagement from './pages/UserManagement';
import BrandingSettings from './pages/BrandingSettings';
import ApprovalMatrix from './pages/ApprovalMatrix';
import CreateContract from './pages/CreateContract';
import UserGuide from './pages/UserGuide';
import ChangePassword from './pages/ChangePassword';
import MyDelegation from './pages/MyDelegation';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';

// Ensure axios sends cookies globally
axios.defaults.withCredentials = true;

const AppShell = () => {
  const [currentFY, setCurrentFY] = useState('');
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      axios.get(`${API}/financial-year`).then(res => setCurrentFY(res.data.fy)).catch(() => {});
    }
  }, [user]);

  // Login / callback / magic routes — no shell
  const PUBLIC_PATHS = ['/login', '/auth/callback', '/magic'];
  if (PUBLIC_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/magic" element={<MagicApprove />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </>
    );
  }

  return (
    <div className="app-container">
      <Sidebar currentFY={currentFY} user={user} onLogout={logout} loading={loading} />
      <main className="main-content">
        <div className="content">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute permission="contracts.create"><UploadProposal /></ProtectedRoute>} />
            <Route path="/create" element={<ProtectedRoute permission="contracts.create"><CreateContract /></ProtectedRoute>} />
            <Route path="/contracts/:id/edit" element={<ProtectedRoute><CreateContract /></ProtectedRoute>} />
            <Route path="/contracts" element={<ProtectedRoute><ContractsList /></ProtectedRoute>} />
            <Route path="/contracts/:id" element={<ProtectedRoute><ContractDetail /></ProtectedRoute>} />
            <Route path="/amendments/:id" element={<ProtectedRoute><AmendmentDetail /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute><TemplateEditor /></ProtectedRoute>} />
            <Route path="/import" element={<ProtectedRoute permission="contracts.create"><BulkImport /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactDirectory /></ProtectedRoute>} />
            <Route path="/export-format" element={<ProtectedRoute><ExportFormatEditor /></ProtectedRoute>} />
            <Route path="/data" element={<ProtectedRoute permission="data.manage"><DataManagement /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute permission="users.manage"><UserManagement /></ProtectedRoute>} />
            <Route path="/branding" element={<ProtectedRoute permission="branding.manage"><BrandingSettings /></ProtectedRoute>} />
            <Route path="/approval-matrix" element={<ProtectedRoute permission="matrix.manage"><ApprovalMatrix /></ProtectedRoute>} />
            <Route path="/my-delegation" element={<ProtectedRoute><MyDelegation /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          </Routes>
        </div>
      </main>
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
